/**
 * Student Management Routes
 * Routes for students, families, and guardians
 */

import { lazy } from "react";
import { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { LazyPage } from "../helpers";

// Students
const StudentsListPage = lazy(() => import("@/pages/students/StudentsListPage").then(m => ({ default: m.StudentsListPage })));
const StudentFormPage = lazy(() => import("@/pages/students/StudentFormPage").then(m => ({ default: m.StudentFormPage })));
const StudentDetailPage = lazy(() => import("@/pages/students/StudentDetailPage").then(m => ({ default: m.StudentDetailPage })));

// Families
const FamiliesListPage = lazy(() => import("@/pages/families/FamiliesListPage").then(m => ({ default: m.FamiliesListPage })));
const FamilyFormPage = lazy(() => import("@/pages/families/FamilyFormPage").then(m => ({ default: m.FamilyFormPage })));
const FamilyDetailPage = lazy(() => import("@/pages/families/FamilyDetailPage").then(m => ({ default: m.FamilyDetailPage })));
const AddGuardianToFamilyPage = lazy(() => import("@/pages/families/AddGuardianToFamilyPage"));
const FamilyStatementPage = lazy(() => import("@/pages/families/FamilyStatementPage"));

// Guardians
const GuardiansListPage = lazy(() => import("@/pages/guardians/GuardiansListPage").then(m => ({ default: m.GuardiansListPage })));
const GuardianDetailPage = lazy(() => import("@/pages/guardians/GuardianDetailPage"));
const LinkGuardianToFamilyPage = lazy(() => import("@/pages/guardians/LinkGuardianToFamilyPage"));

// Payment Methods (Family-specific)
const PaymentMethodsListPage = lazy(() => import("@/pages/payment-methods/PaymentMethodsListPage"));
const AddPaymentMethodPage = lazy(() => import("@/pages/payment-methods/AddPaymentMethodPage"));

export const studentRoutes: RouteObject[] = [
  // Student Routes
  {
    path: "/students",
    element: (
      <ProtectedRoute permission="manage:students">
        <AppShell>
          <LazyPage><StudentsListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/students/new",
    element: (
      <ProtectedRoute permission="manage:students">
        <AppShell>
          <LazyPage><StudentFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/students/:id/edit",
    element: (
      <ProtectedRoute permission="manage:students">
        <AppShell>
          <LazyPage><StudentFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/students/:id",
    element: (
      <ProtectedRoute permission="manage:students">
        <AppShell>
          <LazyPage><StudentDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Family Routes
  {
    path: "/families",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><FamiliesListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/families/new",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><FamilyFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/families/:id/edit",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><FamilyFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/families/:id",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><FamilyDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/families/:id/guardians/add",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><AddGuardianToFamilyPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/families/:id/statement",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><FamilyStatementPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/families/:familyId/payment-methods",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><PaymentMethodsListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/families/:familyId/payment-methods/add",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><AddPaymentMethodPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Guardian Routes
  {
    path: "/guardians",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><GuardiansListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/guardians/:id",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><GuardianDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/guardians/:id/link-family",
    element: (
      <ProtectedRoute permission="manage:families">
        <AppShell>
          <LazyPage><LinkGuardianToFamilyPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
];
